import React, { useState, useEffect } from 'react';
import { Skeleton } from 'antd';
import {
  type BrevoSyncEstadoResponse,
  type BrevoSyncEjecucion,
  type BrevoSyncRitmoPunto,
  computeFaseActual,
  transcurridoSegundos,
  formatDuracion,
  porcentajeFase,
  fmtHoraMadrid,
  ESPERA_ARRANQUE_SEGUNDOS,
} from '../../api/brevoSyncApi';
import { FctAviso, FctProgresoLargo, fmtNumero, type FctTipoAviso } from '../../fct/react';

interface StatusBannerProps {
  estado: BrevoSyncEstadoResponse | null;
  ritmo?: BrevoSyncRitmoPunto[];
  loading: boolean;
}

type VisualState = 'idle' | 'initializing' | 'slow_load' | 'in_progress' | 'stuck' | 'completed_ok' | 'completed_errors' | 'failed' | 'cancelled';

function getVisualState(estado: BrevoSyncEstadoResponse | null, ritmo?: BrevoSyncRitmoPunto[]): VisualState {
  if (!estado) return 'idle';

  if (estado.running) {
    const r = estado.running;
    const elapsed = transcurridoSegundos(r);
    const fase = computeFaseActual(r);

    // Pendiente: el motor aún no la ha cogido. Tras un reinicio tarda hasta unos 5 minutos en arrancar, y no hay latido
    // que mirar: solo pasados ESPERA_ARRANQUE_SEGUNDOS se avisa de que no empieza (#245).
    if (r.estado === 'pendiente') {
      return elapsed >= ESPERA_ARRANQUE_SEGUNDOS ? 'slow_load' : 'initializing';
    }

    if (fase === 'loading') {
      return elapsed >= 300 ? 'slow_load' : 'initializing';
    }

    // Detección cuelgue: por el latido del motor (#236), o por el ritmo si aún no hay latido.
    if (r.ultimoLatido && elapsed > 300) {
      const minSinLatido = (new Date(r.ahora).getTime() - new Date(r.ultimoLatido).getTime()) / 60000;
      return minSinLatido >= 3 ? 'stuck' : 'in_progress';
    }
    if (ritmo && ritmo.length > 0 && elapsed > 300) {
      const ahora = new Date(r.ahora).getTime();
      const ultimoActivo = ritmo
        .filter(p => p.contactos > 0)
        .map(p => new Date(p.minuto).getTime())
        .reduce((a, b) => Math.max(a, b), 0);
      if (ultimoActivo > 0) {
        const minSinActividad = (ahora - ultimoActivo) / 60000;
        if (minSinActividad >= 3) return 'stuck';
      }
    }

    return 'in_progress';
  }

  if (estado.ultimaFinalizada) {
    const uf = estado.ultimaFinalizada;
    if (uf.estado === 'error') return 'failed';
    if (uf.estado === 'cancelada') return 'cancelled';
    if (uf.faseAErrores > 0 || uf.faseBErrores > 0) return 'completed_errors';
    return 'completed_ok';
  }

  return 'idle';
}

function formatRelativeTime(isoDate: string, ahora: string): string {
  const diff = (new Date(ahora).getTime() - new Date(isoDate).getTime()) / 1000;
  if (diff < 60) return 'hace unos segundos';
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} horas`;
  return `hace ${Math.floor(diff / 86400)} días`;
}

/** Hora de inicio «09:30», en hora de Madrid. */
const horaCorta = fmtHoraMadrid;

/** Duración inicio→fin de una ejecución finalizada. */
const duracionEjecucion = (uf: BrevoSyncEjecucion) =>
  formatDuracion(Math.floor((new Date(uf.fin!).getTime() - new Date(uf.inicio).getTime()) / 1000));

/**
 * Tick cada segundo para interpolar el tiempo transcurrido entre polls.
 * Calibrado con el reloj del servidor (r.ahora) para evitar drift por diferencia de relojes.
 */
function useLocalElapsed(r: BrevoSyncEjecucion | undefined): string {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!r) return;
    setTick(0);
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [r?.id, r?.ahora]);

  if (!r) return '';
  const serverElapsed = transcurridoSegundos(r);
  return formatDuracion(Math.max(0, serverElapsed + tick));
}

/**
 * Lo que se pinta para cada estado visual (GCVSC-I001 R-15): la sincronización en marcha es un
 * proceso largo (`FctProgresoLargo`: título, porcentaje si se conoce, métricas y transcurrido);
 * los avisos mientras corre y el resultado final son bandas (`FctAviso`) con el semáforo de antes.
 * Textos y datos, los mismos que el `Alert` anterior; solo cambia dónde va cada dato.
 */
export type BannerConfig =
  | { modo: 'progreso'; titulo: string; estado: 'en-curso' | 'completado'; porcentaje?: number; metricas: { etiqueta: string; valor: string }[]; transcurrido?: string }
  | { modo: 'aviso'; tipo: FctTipoAviso; titulo: string; descripcion: string };

export function configurarBanner(vs: VisualState, estado: BrevoSyncEstadoResponse | null, liveElapsed: string): BannerConfig {
  const r = estado?.running;
  const uf = estado?.ultimaFinalizada;

  switch (vs) {
    case 'idle':
      return {
        modo: 'aviso', tipo: 'exito',
        titulo: uf ? `Última sincronización: ${formatRelativeTime(uf.fin!, uf.ahora)}` : 'Sin ejecuciones registradas',
        descripcion: uf ? `${fmtNumero(uf.faseAExitos + uf.faseBExitos)} contactos · ${duracionEjecucion(uf)} · ejecución ${horaCorta(uf.inicio)}` : '',
      };
    case 'initializing':
      return {
        modo: 'progreso', estado: 'en-curso',
        titulo: r?.estado === 'pendiente' ? 'Esperando a que el motor empiece' : 'Cargando datos del CRM',
        metricas: r
          ? [r.estado === 'pendiente'
            ? { etiqueta: 'El motor tarda en arrancar', valor: 'hasta unos 5 minutos' }
            : { etiqueta: 'Esta fase tarda', valor: 'unos 3 minutos' }]
          : [],
        transcurrido: r ? liveElapsed : undefined,
      };
    case 'slow_load':
      return {
        modo: 'aviso', tipo: 'aviso',
        titulo: r?.estado === 'pendiente' ? 'El motor no ha empezado la sincronización' : 'La carga inicial está tardando más de lo habitual',
        descripcion: r
          ? (r.estado === 'pendiente' ? `Pedida hace ${liveElapsed}. Si sigue así, Sistemas puede pararla.` : `Transcurrido ${liveElapsed}`)
          : '',
      };
    case 'in_progress': {
      const fase = r ? computeFaseActual(r) : null;
      const pct = r
        ? (fase === 'A'
          ? porcentajeFase(r.faseATotal, r.faseAExitos, r.faseAErrores)
          : porcentajeFase(r.faseBTotal, r.faseBExitos, r.faseBErrores))
        : null;
      return {
        modo: 'progreso', estado: 'en-curso',
        titulo: r ? `Fase ${fase} · ${pct != null ? pct + '%' : 'calculando…'}` : 'En curso',
        porcentaje: pct ?? undefined,
        metricas: r ? [{ etiqueta: 'Iniciada a las', valor: horaCorta(r.inicio) }] : [],
        transcurrido: r ? liveElapsed : undefined,
      };
    }
    case 'stuck':
      return {
        modo: 'aviso', tipo: 'aviso',
        titulo: 'Sin progreso reciente',
        descripcion: r ? `La sincronización lleva ${liveElapsed} en marcha` : '',
      };
    case 'completed_ok':
      return {
        modo: 'progreso', estado: 'completado',
        titulo: 'Sincronización completada',
        metricas: uf ? [
          { etiqueta: 'Contactos', valor: fmtNumero(uf.faseAExitos + uf.faseBExitos) },
          { etiqueta: 'Duración', valor: duracionEjecucion(uf) },
          { etiqueta: 'Finalizada', valor: formatRelativeTime(uf.fin!, uf.ahora) },
        ] : [],
      };
    case 'completed_errors':
      return {
        modo: 'aviso', tipo: 'aviso',
        titulo: uf ? `Completada con ${fmtNumero(uf.faseAErrores + uf.faseBErrores)} errores` : 'Completada con errores',
        descripcion: uf ? `${fmtNumero(uf.faseAExitos + uf.faseBExitos)} contactos sincronizados · los errores se reintentarán en la próxima sincronización` : '',
      };
    case 'cancelled':
      return {
        modo: 'aviso', tipo: 'info',
        titulo: 'La última sincronización se paró a petición',
        descripcion: uf ? `${fmtNumero(uf.faseAExitos + uf.faseBExitos)} contactos sincronizados antes de parar` : '',
      };
    case 'failed':
    default:
      return {
        modo: 'aviso', tipo: 'error',
        titulo: 'La sincronización falló',
        descripcion: uf?.errorDetalle ? uf.errorDetalle.split('\n')[0] : 'Error desconocido',
      };
  }
}

/** Pinta la configuración; `acciones` van a la derecha del título (progreso) o tras la descripción (aviso). */
export const BannerSync: React.FC<{ cfg: BannerConfig; acciones?: React.ReactNode }> = ({ cfg, acciones }) => {
  if (cfg.modo === 'progreso') {
    return (
      <FctProgresoLargo
        titulo={cfg.titulo}
        estado={cfg.estado}
        porcentaje={cfg.porcentaje}
        metricas={cfg.metricas}
        transcurrido={cfg.transcurrido}
        acciones={acciones}
      />
    );
  }
  return (
    <FctAviso tipo={cfg.tipo} titulo={cfg.titulo}>
      {cfg.descripcion}
      {acciones}
    </FctAviso>
  );
};

const StatusBanner: React.FC<StatusBannerProps> = ({ estado, ritmo, loading }) => {
  const liveElapsed = useLocalElapsed(estado?.running);

  if (loading && !estado) return <Skeleton active paragraph={{ rows: 1 }} style={{ marginBottom: 16 }} />;

  const vs = getVisualState(estado, ritmo);
  const cfg = configurarBanner(vs, estado, liveElapsed);

  return (
    <div style={{ marginBottom: 16 }}>
      <BannerSync cfg={cfg} />
    </div>
  );
};

export { getVisualState, useLocalElapsed };
export type { VisualState };
export default StatusBanner;
